import { Link } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2, ZoomIn, Sparkles, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Lightbox, useLightbox } from "@/components/Lightbox";
import {
  CornerBracket,
  DotGrid,
  FloatingShape,
  NumberWatermark,
  DiagonalLines,
  DecoStamp,
} from "@/components/Deco";
import { COMPANY } from "@/lib/site-data";

/**
 * Reusable layout for sub-pages with rich decorative hero,
 * lightbox gallery, and CTA strip.
 */
export const SubPageLayout = ({
  eyebrow,
  title,
  intro,
  heroImage,
  bullets = [],
  gallery = [],
  pageNumber = "01",
  children,
}) => {
  const galleryItems = gallery.map((url, i) => ({
    url,
    label: `${title} #${i + 1}`,
  }));
  const lb = useLightbox(galleryItems);

  return (
    <div className="bg-white">
      {/* Hero with rich decorations */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-28 overflow-hidden">
        {/* Background layers */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#F8FAFC] via-white to-white" />
        <DotGrid className="absolute top-20 right-0 w-96 h-96 -z-0 opacity-70" size={18} />
        <FloatingShape className="-top-32 -left-32 w-[500px] h-[500px]" color="#1E3E62" />
        <FloatingShape className="-top-20 right-1/3 w-72 h-72" color="#EA580C" />
        <NumberWatermark n={pageNumber} className="hidden md:block top-4 -right-6" />
        <DiagonalLines className="absolute top-0 left-0 right-0 h-2 text-[#EA580C]" opacity={0.4} />

        <div className="relative max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-6"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 shadow-sm">
                <span className="font-heading font-extrabold text-[#EA580C] text-xs tracking-widest">
                  {pageNumber}
                </span>
                <span className="w-px h-3 bg-gray-300" />
                <span className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
                  {eyebrow}
                </span>
              </div>
              <h1 className="mt-5 font-heading font-extrabold text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {title}
              </h1>
              {intro && (
                <p className="mt-5 text-gray-600 text-base sm:text-lg leading-relaxed max-w-xl">
                  {intro}
                </p>
              )}
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  asChild
                  data-testid="subpage-cta-quote"
                  className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold h-12 px-7 glow-pulse"
                >
                  <Link to="/#ajanlat">
                    Ajánlatot kérek
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <a
                  data-testid="subpage-cta-phone"
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-md border-2 border-[#1E3E62] text-[#1E3E62] hover:bg-[#1E3E62] hover:text-white font-semibold transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  {COMPANY.phone}
                </a>
              </div>

              {/* Trust line */}
              <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#EA580C]" />
                  Munkáinkra garanciát vállalunk
                </span>
                <span className="text-gray-300">·</span>
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-[#EA580C]" />
                  Minőségi alapanyagok
                </span>
                <span className="text-gray-300 hidden sm:inline">·</span>
                <span>20+ év szakmai múlt</span>
              </div>
            </motion.div>

            {heroImage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="lg:col-span-6 relative"
              >
                {/* Decorative frame layers */}
                <div className="absolute -top-6 -right-6 w-full h-full border-2 border-[#EA580C]/30 rounded-lg pointer-events-none hidden md:block" />
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#1E3E62]/[0.06] rounded-lg pointer-events-none hidden md:block" />
                <DotGrid className="absolute -bottom-10 -right-10 w-32 h-32 opacity-80 pointer-events-none hidden md:block" />

                <div className="relative rounded-lg overflow-hidden shadow-2xl aspect-[4/3] bg-gray-200 group">
                  <img
                    src={heroImage}
                    alt={title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#0B192C]/30 via-transparent to-transparent" />
                  <CornerBracket position="tl" />
                  <CornerBracket position="br" />
                </div>

                {/* Floating badge */}
                <div className="absolute -bottom-5 -right-3 md:-right-8 bg-white rounded-md px-5 py-3 shadow-2xl border-l-4 border-[#EA580C] hidden md:block float-anim">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                    Kivitelezés
                  </div>
                  <div className="font-heading font-bold text-[#0B192C]">
                    Garanciával
                  </div>
                </div>

                {/* Stamp top-right */}
                <div className="absolute -top-8 -right-8 hidden lg:block">
                  <DecoStamp />
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Bullets with decorated section */}
      {bullets.length > 0 && (
        <section className="py-16 md:py-24 bg-white border-t border-gray-100 relative overflow-hidden">
          <DotGrid className="absolute -top-10 left-1/2 -translate-x-1/2 w-[600px] h-32 opacity-40" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {bullets.map((b, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`subpage-bullet-${i}`}
                  className="group relative bg-white p-6 rounded-lg border border-gray-200 hover:border-[#EA580C] hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                  <span className="absolute top-3 right-3 text-[10px] font-heading font-extrabold text-[#EA580C]/30 tracking-widest">
                    0{i + 1}
                  </span>
                  <div className="w-10 h-10 rounded-md bg-[#EA580C]/10 text-[#EA580C] flex items-center justify-center group-hover:bg-[#EA580C] group-hover:text-white transition-colors">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <h3 className="mt-4 font-heading font-bold text-[#0B192C] text-lg">
                    {b.title}
                  </h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                    {b.text}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {children}

      {/* Gallery with lightbox */}
      {galleryItems.length > 0 && (
        <section className="py-16 md:py-24 bg-[#F8FAFC] relative overflow-hidden">
          <DiagonalLines className="absolute top-0 left-0 right-0 h-2 text-[#1E3E62]" opacity={0.3} />
          <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
            <div className="flex items-end justify-between max-w-3xl">
              <div>
                <span className="eyebrow">Munkáink képekben</span>
                <h2 className="mt-3 font-heading font-bold text-[#0B192C] text-3xl sm:text-4xl tracking-tight">
                  Néhány korábbi munkánk.
                </h2>
              </div>
              <span className="text-sm text-gray-500 hidden md:block">
                Kattintson a képekre a nagyításhoz →
              </span>
            </div>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {galleryItems.map((img, i) => (
                <motion.button
                  key={i}
                  initial={{ opacity: 0, scale: 0.94 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4, delay: (i % 6) * 0.06 }}
                  onClick={() => lb.openAt(i)}
                  data-testid={`subpage-gallery-${i}`}
                  className="group relative aspect-[4/3] overflow-hidden rounded-lg bg-gray-200"
                >
                  <img
                    src={img.url}
                    alt={img.label}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 text-[#0B192C] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <ZoomIn className="w-4 h-4" />
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
          <Lightbox
            images={galleryItems}
            open={lb.open}
            index={lb.index}
            onClose={lb.close}
            onNav={lb.nav}
          />
        </section>
      )}

      {/* CTA strip with decorations */}
      <section className="bg-[#0B192C] text-white relative overflow-hidden">
        <FloatingShape className="-top-20 -right-20 w-80 h-80" color="#EA580C" />
        <FloatingShape className="-bottom-20 -left-20 w-72 h-72" color="#1E3E62" />
        <DiagonalLines className="absolute inset-0 text-white" opacity={0.04} />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6 relative">
          <div>
            <h3 className="font-heading font-bold text-white text-2xl md:text-3xl">
              Kérjen ingyenes árajánlatot még ma!
            </h3>
            <p className="mt-2 text-gray-300 max-w-xl">
              Ingyenes állapotfelmérés telephelyünkön.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Button
              asChild
              className="bg-[#EA580C] hover:bg-[#C2410C] h-12 px-6 font-semibold glow-pulse"
            >
              <Link to="/#ajanlat">Ajánlatkérés</Link>
            </Button>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md border-2 border-white/30 hover:bg-white hover:text-[#0B192C] font-semibold transition-colors"
            >
              <Phone className="w-4 h-4" />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SubPageLayout;
